import React, { useState, useRef, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faTimes, faDownload, faSpinner, faChevronDown, faPlus, faMinus, faRedo } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import TicketMessageItem from './TicketMessageItem';
import { ConversationContextModal } from './ConversationContextModal';

const TicketMessagesStream = ({
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    onLoadMore,
    error,
    onReply
}) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const [lightboxImage, setLightboxImage] = useState(null);
    const [isLightboxLoading, setIsLightboxLoading] = useState(false);
    const [contextMessageId, setContextMessageId] = useState(null);
    const [contextFromNumber, setContextFromNumber] = useState(null);
    const [isContextModalOpen, setIsContextModalOpen] = useState(false);
    const streamContainerRef = useRef(null);
    const prevScrollHeightRef = useRef(0);
    const isPrependRef = useRef(false);
    const isAtBottomRef = useRef(true);
    const lastMsgIdRef = useRef(null);

    // Estados para el botón flotante y el contador de mensajes nuevos
    const [showScrollBottom, setShowScrollBottom] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    // Estados para Zoom y Desplazamiento (Pan) del visor de imagen
    const [zoomScale, setZoomScale] = useState(1);
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef({ x: 0, y: 0 });


    const [highlightedMessageId, setHighlightedMessageId] = useState(null);

    const handleScrollToMessage = (targetId) => {
        const el = document.getElementById(`ticket-msg-${targetId}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightedMessageId(targetId);
            setTimeout(() => {
                setHighlightedMessageId(null);
            }, 1800);
        }
    };

    const handleOpenContext = (idMensaje, fromNumber = null) => {
        setContextMessageId(idMensaje);
        setContextFromNumber(fromNumber);
        setIsContextModalOpen(true);
    };
    const handleOpenLightbox = (url) => {
        setIsLightboxLoading(true);
        setLightboxImage(url);
        setZoomScale(1);
        setPanPosition({ x: 0, y: 0 });
    };

    const handleCloseLightbox = () => {
        setLightboxImage(null);
        setZoomScale(1);
        setPanPosition({ x: 0, y: 0 });
        setIsDragging(false);
    };

    const handleZoomIn = (e) => {
        e?.stopPropagation();
        setZoomScale(prev => Math.min(Number((prev + 0.5).toFixed(1)), 4));
    };

    const handleZoomOut = (e) => {
        e?.stopPropagation();
        setZoomScale(prev => {
            const next = Math.max(Number((prev - 0.5).toFixed(1)), 1);
            if (next === 1) setPanPosition({ x: 0, y: 0 });
            return next;
        });
    };

    const handleResetZoom = (e) => {
        e?.stopPropagation();
        setZoomScale(1);
        setPanPosition({ x: 0, y: 0 });
    };

    const handleWheelZoom = (e) => {
        e.preventDefault();
        if (e.deltaY < 0) {
            setZoomScale(prev => Math.min(Number((prev + 0.25).toFixed(2)), 4));
        } else {
            setZoomScale(prev => {
                const next = Math.max(Number((prev - 0.25).toFixed(2)), 1);
                if (next === 1) setPanPosition({ x: 0, y: 0 });
                return next;
            });
        }
    };

    const handleMouseDown = (e) => {
        if (zoomScale <= 1) return;
        e.preventDefault();
        setIsDragging(true);
        dragStartRef.current = {
            x: e.clientX - panPosition.x,
            y: e.clientY - panPosition.y
        };
    };

    const handleMouseMove = (e) => {
        if (!isDragging || zoomScale <= 1) return;
        e.preventDefault();
        setPanPosition({
            x: e.clientX - dragStartRef.current.x,
            y: e.clientY - dragStartRef.current.y
        });
    };

    const handleMouseUp = () => setIsDragging(false);

    // Manejo del scroll ante cambios en la lista de mensajes
    useEffect(() => {
        const container = streamContainerRef.current;
        if (!container) return;

        if (isPrependRef.current) {
            const diff = container.scrollHeight - prevScrollHeightRef.current;
            container.scrollTop = diff;
            isPrependRef.current = false;
        } else {
            const lastMsg = messages[messages.length - 1];
            const lastId = lastMsg?.idMensaje || lastMsg?.id;
            const prevLastId = lastMsgIdRef.current;

            // Si es la carga inicial o el usuario está al fondo del chat
            if (isAtBottomRef.current || !prevLastId) {
                container.scrollTop = container.scrollHeight;
                setShowScrollBottom(false);
                setUnreadCount(0);
            } else if (lastId && lastId !== prevLastId) {
                // Buscamos dónde estaba el último mensaje que teníamos
                const prevIndex = messages.findIndex(m => (m.idMensaje || m.id) === prevLastId);

                if (prevIndex !== -1) {
                    // Tomamos únicamente los mensajes que cayeron AL FONDO después de ese
                    const newlyArrived = messages.slice(prevIndex + 1);

                    // Filtramos que sean del cliente (para no sumar lo que tú mismo envíes)
                    const clientNew = newlyArrived.filter(m => !m.bEsAgente && m.bEsAgente !== 1);

                    if (clientNew.length > 0) {
                        setUnreadCount(prev => prev + clientNew.length);
                        setShowScrollBottom(true);
                    }
                }
            }

            // Actualizamos el ID del último mensaje
            lastMsgIdRef.current = lastId;
        }
    }, [messages]);

    // Infinite scroll automático hacia arriba al llegar cerca del tope
    const handleScroll = () => {
        const container = streamContainerRef.current;
        if (!container) return;

        // Detectar si el usuario está cerca del fondo (margen de 80px)
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        const isNearBottom = distanceFromBottom < 80;
        isAtBottomRef.current = isNearBottom;

        if (isNearBottom) {
            setShowScrollBottom(false);
            setUnreadCount(0);
        } else {
            setShowScrollBottom(true);
        }

        // Infinite scroll automático hacia arriba al llegar cerca del tope
        if (isLoadingMore || !hasMore || isLoading) return;
        if (container.scrollTop < 60) {
            prevScrollHeightRef.current = container.scrollHeight;
            isPrependRef.current = true;
            onLoadMore();
        }
    };

    // Función al hacer click en la flecha flotante
    const scrollToBottom = () => {
        const container = streamContainerRef.current;
        if (!container) return;

        container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
        });
        setUnreadCount(0);
        setShowScrollBottom(false);
    };

    return (
        <div className="position-relative flex-grow-1 d-flex flex-column overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
            {/* Contenedor principal con scroll vertical */}
            <div
                ref={streamContainerRef}
                onScroll={handleScroll}
                className={`flex-grow-1 p-4 d-flex flex-column gap-3 se-custom-scrollbar ${isDark ? 'se-dark-scrollbar' : ''}`}
                style={{
                    backgroundColor: isDark ? '#0b141a' : '#ffffff',
                    height: '100%',
                    minHeight: 0,
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: isDark ? '#2b394a transparent' : '#cbd5e1 transparent'
                }}
            >
                {/* Botón / Spinner de carga para mensajes anteriores */}
                {isLoadingMore && (
                    <div className="text-center text-muted my-2" style={{ fontSize: '0.8rem' }}>
                        <div className="spinner-border spinner-border-sm me-2 text-success" role="status"></div>
                        Cargando mensajes anteriores...
                    </div>
                )}

                {hasMore && !isLoadingMore && !isLoading && messages.length >= 30 && (
                    <div className="text-center my-1">
                        <button
                            type="button"
                            className="btn btn-sm btn-link text-muted text-decoration-none shadow-none"
                            style={{ fontSize: '0.75rem' }}
                            onClick={onLoadMore}
                        >
                            ▲ Cargar mensajes anteriores
                        </button>
                    </div>
                )}

                {isLoading && (
                    <div className="text-center text-muted my-3" style={{ fontSize: '0.85rem' }}>
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        Cargando mensajes...
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger mx-3 my-2 text-center" style={{ fontSize: '0.85rem' }} role="alert">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
                        {error}
                    </div>
                )}

                {!isLoading && !error && messages.length === 0 && (
                    <div className="text-center text-muted my-3" style={{ fontSize: '0.85rem' }}>
                        No hay mensajes en este ticket aún.
                    </div>
                )}

                {/* Centered Date Tag */}
                {messages.length > 0 && (
                    <div className="text-center my-1">
                        <span
                            className="px-3 py-1 rounded-pill fw-semibold"
                            style={{
                                backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                                color: isDark ? '#94a3b8' : '#64748b',
                                fontSize: '0.72rem'
                            }}
                        >
                            Historial de Conversación
                        </span>
                    </div>
                )}

                {/* Lista de Mensajes */}
                {messages.map((msg, index) => (
                    <TicketMessageItem
                        key={msg.idMensaje || msg.id || index}
                        msg={msg}
                        index={index}
                        messages={messages}
                        onOpenLightbox={handleOpenLightbox}
                        onReply={onReply}
                        onOpenContext={handleOpenContext}
                        onScrollToMessage={handleScrollToMessage}
                        isHighlighted={highlightedMessageId === (msg.idMensaje || msg.id)}
                    />
                ))}
            </div>

            {/* Modal Lightbox Viewer centrado al contenedor del chat con soporte de Zoom y Arrastre */}
            {lightboxImage && (
                <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
                    style={{
                        backgroundColor: 'rgba(15, 23, 42, 0.90)',
                        zIndex: 9999,
                        backdropFilter: 'blur(5px)'
                    }}
                    onClick={handleCloseLightbox}
                >
                    <div
                        className="position-relative d-flex flex-column align-items-center justify-content-between w-100 h-100"
                        style={{ maxWidth: '100%', maxHeight: '100%' }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Spinner de carga para la imagen en alta resolución */}
                        {isLightboxLoading && (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                <FontAwesomeIcon icon={faSpinner} spin className="text-white mb-2" style={{ fontSize: '2rem' }} />
                                <span className="text-white-50" style={{ fontSize: '0.85rem' }}>Cargando imagen...</span>
                            </div>
                        )}

                        {/* Contenedor interactivo de la Imagen con Zoom y Drag */}
                        <div
                            className="d-flex align-items-center justify-content-center overflow-hidden position-relative rounded-3 flex-grow-1 w-100"
                            style={{
                                minHeight: 0,
                                cursor: zoomScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                                userSelect: 'none'
                            }}
                            onWheel={handleWheelZoom}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onDoubleClick={() => setZoomScale(prev => (prev > 1 ? 1 : 2))}
                        >
                            <img
                                src={lightboxImage}
                                alt="Vista ampliada"
                                className="img-fluid rounded-2 shadow-lg"
                                draggable={false}
                                style={{
                                    maxHeight: '100%',
                                    maxWidth: '100%',
                                    margin: 'auto',
                                    objectFit: 'contain',
                                    display: isLightboxLoading ? 'none' : 'block',
                                    transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomScale})`,
                                    transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                                    transformOrigin: 'center center'
                                }}
                                onLoad={() => setIsLightboxLoading(false)}
                                onError={() => setIsLightboxLoading(false)}
                            />
                        </div>

                        {/* Barra de herramientas inferior con Zoom, Descargar y Cerrar */}
                        <div
                            className="d-flex align-items-center justify-content-between px-3 py-2 rounded-pill shadow-lg mt-3"
                            style={{
                                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.25)',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
                                gap: '14px',
                                zIndex: 10000
                            }}
                        >
                            {/* Controles de Zoom con alto contraste */}
                            <div className="d-flex align-items-center gap-1.5">
                                {/* Botón Alejar (-) */}
                                <button
                                    type="button"
                                    className="btn btn-sm d-flex align-items-center justify-content-center p-0 shadow-none"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        backgroundColor: zoomScale <= 1 ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.20)',
                                        border: '1px solid rgba(255, 255, 255, 0.25)',
                                        color: zoomScale <= 1 ? 'rgba(255, 255, 255, 0.35)' : '#ffffff',
                                        cursor: zoomScale <= 1 ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.15s ease'
                                    }}
                                    onClick={handleZoomOut}
                                    disabled={zoomScale <= 1}
                                    title="Alejar (-)"
                                >
                                    <FontAwesomeIcon icon={faMinus} style={{ fontSize: '0.85rem' }} />
                                </button>

                                <span
                                    className="px-2 fw-bold text-center user-select-none"
                                    style={{
                                        fontSize: '0.82rem',
                                        minWidth: '50px',
                                        color: '#ffffff',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                                    }}
                                >
                                    {Math.round(zoomScale * 100)}%
                                </span>

                                {/* Botón Acercar (+) */}
                                <button
                                    type="button"
                                    className="btn btn-sm d-flex align-items-center justify-content-center p-0 shadow-none"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        backgroundColor: zoomScale >= 4 ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.20)',
                                        border: '1px solid rgba(255, 255, 255, 0.25)',
                                        color: zoomScale >= 4 ? 'rgba(255, 255, 255, 0.35)' : '#ffffff',
                                        cursor: zoomScale >= 4 ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.15s ease'
                                    }}
                                    onClick={handleZoomIn}
                                    disabled={zoomScale >= 4}
                                    title="Acercar (+)"
                                >
                                    <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.85rem' }} />
                                </button>

                                {/* Botón Reset */}
                                {zoomScale > 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm rounded-pill px-2.5 py-0 ms-1 d-flex align-items-center gap-1 shadow-none"
                                        style={{
                                            fontSize: '0.74rem',
                                            height: '28px',
                                            backgroundColor: 'rgba(56, 189, 248, 0.2)',
                                            border: '1px solid #38bdf8',
                                            color: '#38bdf8',
                                            fontWeight: 600,
                                            cursor: 'pointer'
                                        }}
                                        onClick={handleResetZoom}
                                        title="Restablecer tamaño original"
                                    >
                                        <FontAwesomeIcon icon={faRedo} style={{ fontSize: '0.65rem' }} />
                                        Reset
                                    </button>
                                )}
                            </div>

                            <div style={{ width: '1px', height: '22px', backgroundColor: 'rgba(255, 255, 255, 0.25)' }} />

                            {/* Acciones de Descarga y Cierre */}
                            <div className="d-flex align-items-center gap-2">
                                <a
                                    href={lightboxImage}
                                    download="imagen_whatsapp.jpg"
                                    className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        backgroundColor: '#ffffff',
                                        color: '#0f172a',
                                        border: 'none',
                                        textDecoration: 'none'
                                    }}
                                    title="Descargar imagen"
                                >
                                    <FontAwesomeIcon icon={faDownload} style={{ fontSize: '0.85rem' }} />
                                </a>
                                <button
                                    type="button"
                                    className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        backgroundColor: '#ef4444',
                                        color: '#ffffff',
                                        border: 'none'
                                    }}
                                    onClick={handleCloseLightbox}
                                    title="Cerrar (Esc)"
                                >
                                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.9rem' }} />
                                </button>
                            </div>
                        </div>

                        {/* Ayuda visual cuando hay zoom */}
                        {zoomScale > 1 && (
                            <span className="text-white-50 mt-1" style={{ fontSize: '0.75rem' }}>
                                💡 Arrastra con el ratón para moverte por la imagen · Doble clic para alternar zoom
                            </span>
                        )}
                    </div>
                </div>
            )}

            <ConversationContextModal
                isOpen={isContextModalOpen}
                onClose={() => {
                    setIsContextModalOpen(false);
                    setContextFromNumber(null);
                }}
                messageId={contextMessageId}
                fromNumber={contextFromNumber}
            />

            {/* Botón flotante estilo WhatsApp con flecha y contador de mensajes nuevos */}
            {showScrollBottom && (
                <div
                    className="position-absolute"
                    style={{
                        bottom: '20px',
                        right: '24px',
                        zIndex: 10
                    }}
                >
                    <button
                        type="button"
                        onClick={scrollToBottom}
                        className="btn position-relative rounded-circle shadow-lg d-flex align-items-center justify-content-center border-0 p-0"
                        style={{
                            width: '42px',
                            height: '42px',
                            backgroundColor: isDark ? '#202c33' : '#ffffff',
                            color: isDark ? '#aebac1' : '#54656f',
                            boxShadow: isDark
                                ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                                : '0 4px 12px rgba(0, 0, 0, 0.15)',
                            cursor: 'pointer',
                            transition: 'transform 0.15s ease, background-color 0.15s ease'
                        }}
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.06)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                        title="Ir al último mensaje"
                    >
                        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '0.85rem' }} />

                        {/* Badge verde con la cantidad de nuevos mensajes */}
                        {unreadCount > 0 && (
                            <span
                                className="position-absolute top-0 start-50 translate-middle badge rounded-pill"
                                style={{
                                    backgroundColor: '#25d366',
                                    color: '#ffffff',
                                    fontSize: '0.68rem',
                                    fontWeight: '600',
                                    padding: '0.22em 0.5em',
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.25)'
                                }}
                            >
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default TicketMessagesStream;